import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Plus, Star, Send, Pencil, Trash2, RotateCcw, Lock, Eye, EyeOff,
  Loader2, Mail, Power, PlugZap,
} from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import {
  smtpAccountList as smtpAccountListSchema,
  emailSettings as emailSettingsSchema,
} from '../lib/types'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { TextField, FieldLabel } from './ui/Field'
import { ConfirmDialog } from './ui/ConfirmDialog'

/** Shared input styling — mirrors `ui/Field.jsx`'s `inputBase`. */
const inputBase =
  'w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] ' +
  'bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm ' +
  'placeholder:text-[color:var(--color-ink-subtle)] outline-none ' +
  'transition-colors focus:border-[color:var(--color-accent)]'
const selectBase = `${inputBase} cursor-pointer`
const btnPrimary =
  'shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium ' +
  'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] ' +
  'cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed'
const iconBtn =
  'inline-flex items-center justify-center h-8 w-8 rounded-md text-[color:var(--color-ink-muted)] ' +
  'hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink)] ' +
  'cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed'

/** SMTP provider presets — picking one fills host/port/encryption (still
 *  editable) and relabels the credential fields. Email sends over SMTP either
 *  way (nodemailer), so there's no send-path change. */
const SMTP_PROVIDERS = {
  smtp: { label: "SMTP (generic — any provider's relay)", host: '', port: '587', encryption: 'tls' },
  zoho: { label: 'Zoho / ZeptoMail (SMTP relay)', host: 'smtp.zeptomail.in', port: '587', encryption: 'tls' },
  brevo: { label: 'Brevo (SMTP relay)', host: 'smtp-relay.brevo.com', port: '587', encryption: 'tls' },
  gmail: { label: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: '587', encryption: 'tls' },
  sendgrid: { label: 'SendGrid (SMTP relay)', host: 'smtp.sendgrid.net', port: '587', encryption: 'tls' },
  mailgun: { label: 'Mailgun (SMTP relay)', host: 'smtp.mailgun.org', port: '587', encryption: 'tls' },
  postmark: { label: 'Postmark (SMTP relay)', host: 'smtp.postmarkapp.com', port: '587', encryption: 'tls' },
  ses: { label: 'Amazon SES (SMTP relay)', host: 'email-smtp.us-east-1.amazonaws.com', port: '587', encryption: 'tls' },
}

const STATUS_STYLE = {
  active: 'bg-[color:var(--color-success-soft,#e8f6ee)] text-[color:var(--color-success,#1a7f45)]',
  blocked: 'bg-[color:var(--color-danger-soft,#fdeaea)] text-[color:var(--color-danger,#c0392b)]',
  disabled: 'bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)]',
}

function StatusPill({ status }) {
  const label = status === 'active' ? 'Active' : status === 'blocked' ? 'Blocked' : 'Disabled'
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status] ?? STATUS_STYLE.disabled}`}>
      {label}
    </span>
  )
}

/** Small on/off toggle (no dependency) — used for Primary / Enabled in the modal. */
function Toggle({ checked, onChange, labelOn, labelOff, accent = 'var(--color-primary)' }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 cursor-pointer"
    >
      <span
        className="relative inline-block h-5 w-9 rounded-full transition-colors"
        style={{ background: checked ? accent : 'var(--color-rule-strong)' }}
      >
        <span
          className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
          style={{ left: checked ? '1.125rem' : '0.125rem' }}
        />
      </span>
      <span className="text-xs text-[color:var(--color-ink-muted)]">{checked ? labelOn : labelOff}</span>
    </button>
  )
}

const EMPTY_FORM = {
  label: '', provider: 'smtp', host: '', port: '587', encryption: 'tls',
  authMode: 'login', username: '', password: '', hasPassword: false,
  senderName: '', senderEmail: '', priority: '100', isPrimary: false, enabled: true,
}

/** Add / Edit SMTP account modal. */
function AccountModal({ open, account, onClose, onSaved }) {
  const isEdit = Boolean(account)
  const [form, setForm] = useState(EMPTY_FORM)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})

  // Re-seed the form whenever the modal opens for a (different) account.
  const seedKey = `${open}:${account?.id ?? 'new'}`
  const [lastSeed, setLastSeed] = useState('')
  if (open && seedKey !== lastSeed) {
    setLastSeed(seedKey)
    setErrors({})
    setShowPassword(false)
    setForm(
      account
        ? {
            label: account.label ?? '', provider: 'smtp',
            host: account.host ?? '', port: String(account.port ?? 587),
            encryption: account.encryption ?? 'tls', authMode: account.authMode ?? 'login',
            username: account.username ?? '', password: '', hasPassword: account.hasPassword,
            senderName: account.senderName ?? '', senderEmail: account.senderEmail ?? '',
            priority: String(account.priority ?? 100),
            isPrimary: account.isPrimary, enabled: account.enabled,
          }
        : EMPTY_FORM,
    )
  }

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }))
    if (errors[k]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[k]
        return next
      })
    }
  }
  const onProvider = (next) => {
    const p = SMTP_PROVIDERS[next]
    setForm((f) => ({
      ...f, provider: next,
      ...(next !== 'smtp' && p ? { host: p.host, port: p.port, encryption: p.encryption } : {}),
    }))
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        label: form.label.trim(),
        host: form.host.trim(),
        port: Number(form.port) || 587,
        encryption: form.encryption,
        authMode: form.authMode,
        username: form.username,
        senderName: form.senderName,
        senderEmail: form.senderEmail,
        priority: Number(form.priority) || 100,
        isPrimary: form.isPrimary,
        enabled: form.enabled,
      }
      if (form.password) body.password = form.password
      const res = isEdit
        ? await api.put(`/super-admin/email-settings/accounts/${account.id}`, body)
        : await api.post('/super-admin/email-settings/accounts', body)
      return res.data
    },
    onSuccess: () => {
      toast.success(isEdit ? 'SMTP account updated.' : 'SMTP account added.')
      onSaved()
      onClose()
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields && Object.keys(fields).length > 0) {
        setErrors(fields)
        toast.error('Please fix the highlighted fields.')
      } else toast.error(message)
    },
  })

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? () => {} : onClose}
      title={isEdit ? 'Edit SMTP Account' : 'Add SMTP Account'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!form.label.trim() || !form.host.trim()}
          >
            {isEdit ? 'Update Account' : 'Add Account'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <TextField label="Label (friendly name)" placeholder="e.g. Zoho" value={form.label} onChange={(e) => set('label', e.target.value)} error={errors.label} />
        </div>
        <div className="col-span-2">
          <FieldLabel label="Provider preset" htmlFor="acc-provider" />
          <select id="acc-provider" value={form.provider} onChange={(e) => onProvider(e.target.value)} className={selectBase}>
            {Object.entries(SMTP_PROVIDERS).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
          </select>
        </div>
        <TextField label="SMTP Host" placeholder="smtp.example.com" value={form.host} onChange={(e) => set('host', e.target.value)} error={errors.host} />
        <TextField label="Port" type="number" placeholder="587" value={form.port} onChange={(e) => set('port', e.target.value)} error={errors.port} />
        <div>
          <FieldLabel label="Security" htmlFor="acc-enc" />
          <select id="acc-enc" value={form.encryption} onChange={(e) => set('encryption', e.target.value)} className={selectBase}>
            <option value="tls">TLS</option>
            <option value="ssl">SSL</option>
            <option value="none">None</option>
          </select>
        </div>
        <TextField label="Priority" type="number" placeholder="100" value={form.priority} onChange={(e) => set('priority', e.target.value)} error={errors.priority} />
        <div className="col-span-2">
          <FieldLabel label="Authentication" htmlFor="acc-auth" />
          <select id="acc-auth" value={form.authMode} onChange={(e) => set('authMode', e.target.value)} className={selectBase}>
            <option value="login">Username &amp; password</option>
            <option value="none">None</option>
          </select>
        </div>
        {form.authMode === 'login' ? (
          <>
            <TextField label="Username" placeholder="Username / API key" value={form.username} onChange={(e) => set('username', e.target.value)} error={errors.username} />
            <div>
              <FieldLabel label="Password / API key" htmlFor="acc-pass" />
              <div className="relative">
                <Lock size={16} aria-hidden className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]" />
                <input
                  id="acc-pass" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                  value={form.password} onChange={(e) => set('password', e.target.value)}
                  placeholder={form.hasPassword ? '•••••••• (leave blank to keep current)' : 'Enter password / API key'}
                  className={inputBase + ' pl-10 pr-10'}
                />
                <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide' : 'Show'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer">
                  {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
            </div>
          </>
        ) : null}
        <TextField label="Sender Name" placeholder="TaskMagix" value={form.senderName} onChange={(e) => set('senderName', e.target.value)} error={errors.senderName} />
        <TextField label="Sender Email" type="email" placeholder="noreply@taskmagix.com" value={form.senderEmail} onChange={(e) => set('senderEmail', e.target.value)} error={errors.senderEmail} />
        <div className="rounded-lg bg-[color:var(--color-card-hover)] px-3 py-2.5">
          <p className="text-sm font-medium text-[color:var(--color-ink)]">Set as Primary</p>
          <p className="mb-1.5 text-xs text-[color:var(--color-ink-muted)]">Tried first for every send.</p>
          <Toggle checked={form.isPrimary} onChange={(v) => set('isPrimary', v)} labelOn="Primary" labelOff="Not primary" accent="var(--color-warning,#e0a106)" />
        </div>
        <div className="rounded-lg bg-[color:var(--color-card-hover)] px-3 py-2.5">
          <p className="text-sm font-medium text-[color:var(--color-ink)]">Enabled</p>
          <p className="mb-1.5 text-xs text-[color:var(--color-ink-muted)]">Include in the failover pool.</p>
          <Toggle checked={form.enabled} onChange={(v) => set('enabled', v)} labelOn="Enabled" labelOff="Disabled" />
        </div>
      </div>
    </Modal>
  )
}

/** Send-test modal for one account. */
function TestModal({ open, account, onClose }) {
  const [to, setTo] = useState('')
  const seedKey = `${open}:${account?.id ?? ''}`
  const [lastSeed, setLastSeed] = useState('')
  if (open && seedKey !== lastSeed) { setLastSeed(seedKey); setTo('') }
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())
  const mutation = useMutation({
    mutationFn: async () => api.post(`/super-admin/email-settings/accounts/${account.id}/test`, { recipient: to.trim() }),
    onSuccess: () => { toast.success(`Test email sent to ${to.trim()}.`); onClose() },
    onError: (err) => toast.error(extractApiError(err).message),
  })
  return (
    <Modal open={open} onClose={mutation.isPending ? () => {} : onClose} title={`Send test — ${account?.label ?? ''}`} size="sm"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!valid}>Send Test</Button>
      </>}>
      <FieldLabel label="Send a test email to" htmlFor="acc-test-to" />
      <input id="acc-test-to" type="email" maxLength={320} value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" className={inputBase} />
      <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">Sends a plain test message through this account only (no failover).</p>
    </Modal>
  )
}

/** Platform-wide authenticated sender domains (applies to every account). */
function AuthenticatedDomainsCard() {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ['email-settings'],
    queryFn: async () => emailSettingsSchema.parse((await api.get('/super-admin/email-settings')).data),
  })
  const [value, setValue] = useState(null)
  const settings = query.data
  const current = value ?? settings?.authenticatedDomains ?? ''
  const mutation = useMutation({
    mutationFn: async () => {
      // Preserve the singleton's other fields (it still powers the empty-pool
      // fallback); password omitted = keep. Only the allowlist changes here.
      const body = {
        provider: settings.provider, host: settings.host, port: settings.port,
        encryption: settings.encryption, authMode: settings.authMode,
        username: settings.username, fromName: settings.fromName, fromEmail: settings.fromEmail,
        replyTo: settings.replyTo, authenticatedDomains: current,
      }
      return api.put('/super-admin/email-settings', body)
    },
    onSuccess: () => { toast.success('Authenticated sender domains saved.'); setValue(null); void qc.invalidateQueries({ queryKey: ['email-settings'] }) },
    onError: (err) => toast.error(extractApiError(err).message),
  })
  if (query.isLoading || !settings) return null
  const dirty = value !== null && value !== (settings.authenticatedDomains ?? '')
  return (
    <div className="mt-4 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <div className="mb-2 flex items-center gap-2">
        <PlugZap size={16} className="text-[color:var(--color-accent-strong)]" />
        <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">Authenticated sender domains</p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="flex-1">
          <TextField label={null} placeholder="taskmagix.com" value={current} onChange={(e) => setValue(e.target.value)} />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            Comma-separated domains verified (SPF/DKIM) with your provider. Every account's Sender Email must be on this list or that account is skipped. Leave blank to fall back to the server default.
          </p>
        </div>
        <button type="button" onClick={() => mutation.mutate()} disabled={!dirty || mutation.isPending} className={btnPrimary + ' h-10 mt-6'}>
          {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
        </button>
      </div>
    </div>
  )
}

/**
 * SMTP Settings — the multi-account failover POOL. The platform tries the
 * primary account first and falls over to the next by priority; an account
 * auto-blocks after 3 consecutive failures and is re-activated from Actions.
 * When the pool is empty the server falls back to the legacy single config.
 */
export function SmtpSettings() {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ['smtp-accounts'],
    queryFn: async () => smtpAccountListSchema.parse((await api.get('/super-admin/email-settings/accounts')).data),
  })
  const [modal, setModal] = useState(null) // {mode:'add'} | {mode:'edit', account}
  const [testFor, setTestFor] = useState(null)
  const [deleteFor, setDeleteFor] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const refresh = () => qc.invalidateQueries({ queryKey: ['smtp-accounts'] })

  const action = async (id, fn, okMsg) => {
    setBusyId(id)
    try { await fn(); if (okMsg) toast.success(okMsg); refresh() }
    catch (err) { toast.error(extractApiError(err).message) }
    finally { setBusyId(null) }
  }
  const promote = (a) => action(a.id, () => api.post(`/super-admin/email-settings/accounts/${a.id}/primary`), `"${a.label}" is now primary.`)
  const toggleEnabled = (a) => action(a.id, () => api.post(`/super-admin/email-settings/accounts/${a.id}/${a.enabled ? 'disable' : 'enable'}`), `"${a.label}" ${a.enabled ? 'disabled' : 'enabled'}.`)
  const reactivate = (a) => action(a.id, () => api.post(`/super-admin/email-settings/accounts/${a.id}/reactivate`), `"${a.label}" re-activated.`)

  const deleteMutation = useMutation({
    mutationFn: async () => api.delete(`/super-admin/email-settings/accounts/${deleteFor.id}`),
    onSuccess: () => { toast.success('SMTP account deleted.'); setDeleteFor(null); refresh() },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const accounts = query.data ?? []

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] flex items-center gap-2.5 m-0">
            <Mail size={20} className="text-[color:var(--color-accent-strong)]" />
            SMTP Accounts (Failover Pool)
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Add multiple SMTP accounts — the platform tries the primary first and falls over to the next by priority if one fails, so a blocked sender never stops email going out. An account auto-blocks after 3 consecutive failures.
          </p>
        </div>
        <button type="button" onClick={() => setModal({ mode: 'add' })} className={btnPrimary + ' shrink-0'}>
          <Plus size={16} /> Add SMTP Account
        </button>
      </div>

      {accounts.length > 0 ? (
        <div className="mb-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card-hover)] px-3 py-2 text-xs text-[color:var(--color-ink-muted)]">
          Pool active — {accounts.length} account{accounts.length === 1 ? '' : 's'}. Delete all accounts to revert to the server's single .env / legacy SMTP config.
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-[color:var(--color-rule)]">
        {query.isLoading ? (
          <p className="px-4 py-6 text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
        ) : query.isError ? (
          <p className="px-4 py-6 text-sm text-[color:var(--color-priority-urgent)]">{extractApiError(query.error).message}</p>
        ) : accounts.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-[color:var(--color-ink-muted)]">No SMTP accounts yet. Email uses the server's legacy single config until you add one.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[color:var(--color-rule)] text-left text-xs uppercase tracking-wide text-[color:var(--color-ink-muted)]">
                <th className="px-4 py-2.5 font-semibold">Label</th>
                <th className="px-4 py-2.5 font-semibold">Host</th>
                <th className="px-4 py-2.5 font-semibold">Sender</th>
                <th className="px-4 py-2.5 font-semibold">Priority</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Primary</th>
                <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} className="border-b border-[color:var(--color-rule)] last:border-0">
                  <td className="px-4 py-3 font-medium text-[color:var(--color-ink-strong)]">{a.label}</td>
                  <td className="px-4 py-3 text-[color:var(--color-ink)]">
                    <div>{a.host}:{a.port}</div>
                    <div className="text-xs uppercase text-[color:var(--color-ink-muted)]">{a.encryption}</div>
                  </td>
                  <td className="px-4 py-3 text-[color:var(--color-ink)]">
                    <div className="truncate max-w-[200px]">{a.senderEmail || '—'}</div>
                    {a.senderName ? <div className="text-xs text-[color:var(--color-ink-muted)]">{a.senderName}</div> : null}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-[color:var(--color-ink)]">{a.priority}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={a.status} />
                    {a.status === 'blocked' && a.lastError ? (
                      <div className="mt-0.5 max-w-[220px] truncate text-xs text-[color:var(--color-ink-muted)]" title={a.lastError}>{a.lastError}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    {a.isPrimary ? <Star size={16} className="fill-[color:var(--color-warning,#e0a106)] text-[color:var(--color-warning,#e0a106)]" /> : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {a.status === 'blocked' ? (
                        <button type="button" title="Re-activate" className={iconBtn} disabled={busyId === a.id} onClick={() => reactivate(a)}><RotateCcw size={16} /></button>
                      ) : null}
                      {!a.isPrimary ? (
                        <button type="button" title="Make primary" className={iconBtn} disabled={busyId === a.id} onClick={() => promote(a)}><Star size={16} /></button>
                      ) : null}
                      <button type="button" title={a.enabled ? 'Disable' : 'Enable'} className={iconBtn} disabled={busyId === a.id} onClick={() => toggleEnabled(a)}><Power size={16} /></button>
                      <button type="button" title="Send test" className={iconBtn} onClick={() => setTestFor(a)}><Send size={16} /></button>
                      <button type="button" title="Edit" className={iconBtn} onClick={() => setModal({ mode: 'edit', account: a })}><Pencil size={16} /></button>
                      <button type="button" title="Delete" className={iconBtn + ' hover:text-[color:var(--color-danger,#c0392b)]'} onClick={() => setDeleteFor(a)}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AuthenticatedDomainsCard />

      <AccountModal
        open={Boolean(modal)}
        account={modal?.account}
        onClose={() => setModal(null)}
        onSaved={refresh}
      />
      <TestModal open={Boolean(testFor)} account={testFor} onClose={() => setTestFor(null)} />
      <ConfirmDialog
        open={Boolean(deleteFor)}
        title="Delete SMTP account"
        message={`Delete "${deleteFor?.label}"? Email will fail over to the remaining accounts (or the legacy config if none remain).`}
        confirmLabel="Delete"
        confirming={deleteMutation.isPending}
        onCancel={() => setDeleteFor(null)}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  )
}
